/**
 * Combobox — Searchable dropdown for selecting one or more items from a list.
 *
 * Combobox Component Guidelines
 * - Use Combobox when the list has more than ~7 items or when users benefit from
 *   typing to filter. For short, fixed lists (3-7 items), prefer a Menu or
 *   RadioGroup instead.
 * - Use `ComboboxTriggerButton` for a styled dropdown trigger with chevron.
 *   `ComboboxTrigger` is the unstyled primitive — only use it when rendering a
 *   fully custom trigger element.
 * - Prefer the simple `<Combobox>` for standard single-select with search. Switch
 *   to the composable API when you need multi-select with chips, custom list
 *   rendering, or non-standard layout (e.g. header/footer content).
 * - Group related items with `ComboboxItemGroup` when the list has natural
 *   categories — it helps users scan long lists faster.
 * - Use `startEnhancer` on `ComboboxItem` for icons or color swatches that help
 *   distinguish items visually (e.g. status icons, team avatars).
 * @see https://base-ui.com/react/components/select Base UI Select documentation
 * @see https://ui.shadcn.com/docs/components/combobox shadcn/ui Combobox documentation
 */
import type { EmptyProps } from "@replohq/lattice/components/empty/Empty";
import type { InputProps } from "@replohq/lattice/components/input/Input";
import type { ListItemContentProps } from "@replohq/lattice/components/list-item/ListItem";
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import * as React from "react";

import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { Button } from "@replohq/lattice/components/button/Button";
import { Empty } from "@replohq/lattice/components/empty/Empty";
import { Input } from "@replohq/lattice/components/input/Input";
import { ListItem } from "@replohq/lattice/components/list-item/ListItem";
import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";
import { CheckIcon, ChevronDown, Search, XIcon } from "lucide-react";

type ComboboxRootProps<
  Value,
  Multiple extends boolean | undefined = false,
> = Omit<ComboboxPrimitive.Root.Props<Value, Multiple>, "items"> & {
  /**
   * The items rendered by `ComboboxList`'s render-prop children. Required:
   * lattice's `ComboboxList` only supports the items-driven render-prop form,
   * not statically composed `<ComboboxItem>` children. Omitting this leaves
   * the popover empty.
   */
  items: NonNullable<ComboboxPrimitive.Root.Props<Value, Multiple>["items"]>;
};

function ComboboxRoot<Value, Multiple extends boolean | undefined = false>(
  props: ComboboxRootProps<Value, Multiple>,
) {
  return <ComboboxPrimitive.Root {...props} />;
}

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

interface ComboboxTriggerProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Trigger.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ComboboxTriggerInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    children,
    ...props
  }: ComboboxTriggerProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      data-slot="combobox-trigger"
      className={cn(
        "[&_svg:not([class*='size-'])]:size-4",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {children}
    </ComboboxPrimitive.Trigger>
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ComboboxTrigger = React.forwardRef(ComboboxTriggerInner) as <
  ClassName extends string,
>(
  props: ComboboxTriggerProps<ClassName> &
    React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

type ComboboxTriggerButtonProps<ClassName extends string = string> =
  React.PropsWithChildren<
    Omit<ComboboxPrimitive.Trigger.Props, "className" | "children"> & {
      className?: LayoutClassName<ClassName>;
      UNSAFE_className?: string;
      startEnhancer?: React.ReactNode;
      endEnhancer?: React.ReactNode;
      size?: "sm" | "base";
      isPlaceholder?: boolean;
    }
  >;

const ComboboxTriggerButtonInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    children,
    startEnhancer,
    endEnhancer,
    size = "sm",
    isPlaceholder = false,
    ...props
  }: ComboboxTriggerButtonProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      data-slot="combobox-trigger-button"
      render={
        <Button
          variant="secondary"
          size={size}
          truncate
          startEnhancer={undefined}
          endEnhancer={
            endEnhancer ?? (
              <ChevronDown
                className={cn(
                  "text-muted shrink-0 ml-auto",
                  size === "sm" ? "size-3!" : "size-4!",
                )}
              />
            )
          }
          UNSAFE_className={cn(
            "justify-start bg-sheet hover:bg-surface justify-between data-popup-open:bg-surface",
            UNSAFE_className,
          )}
        />
      }
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      <div className="flex items-center gap-1 overflow-hidden text-placeholder">
        {startEnhancer}
        <span
          className={cn(
            "truncate",
            isPlaceholder ? "text-placeholder" : "text-default",
          )}
        >
          {children}
        </span>
      </div>
    </ComboboxPrimitive.Trigger>
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ComboboxTriggerButton = React.forwardRef(ComboboxTriggerButtonInner) as <
  ClassName extends string,
>(
  props: ComboboxTriggerButtonProps<ClassName> &
    React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

// Whether the surrounding ComboboxContent is rendering as a phone bottom
// sheet, so inner parts (the header input) can adapt without prop plumbing.
const ComboboxMobileSheetContext = React.createContext(false);

function ComboboxInput({
  disabled = false,
  wrapperRef,
  size,
  tone,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, "className" | "size"> & InputProps) {
  // On the phone sheet the input is the sheet's primary control, so it takes
  // the standard field treatment (base size, surface fill) regardless of the
  // compact sizing callers use for anchored desktop popovers.
  const isMobileSheet = React.useContext(ComboboxMobileSheetContext);
  return (
    <ComboboxPrimitive.Input
      render={
        <Input
          disabled={disabled}
          wrapperRef={wrapperRef}
          size={isMobileSheet ? "base" : size}
          tone={isMobileSheet ? "surface" : tone}
        />
      }
      {...props}
    />
  );
}

interface ComboboxContentProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Popup.Props, "className">,
    Pick<
      ComboboxPrimitive.Positioner.Props,
      "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
    > {
  useMobileSheet?: boolean;
  keepMounted?: boolean;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  /**
   * Override the Positioner's tailwind class. Use to bump the stacking order
   * when the combobox renders alongside a high-z-index overlay.
   */
  UNSAFE_positionerClassName?: string;
  /**
   * Override the `<Portal>` target. Mirrors
   * `PopoverContent.UNSAFE_portalContainer`.
   */
  UNSAFE_portalContainer?: ComboboxPrimitive.Portal.Props["container"];
}

function ComboboxContent<ClassName extends string>({
  className,
  UNSAFE_className,
  UNSAFE_positionerClassName,
  UNSAFE_portalContainer,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  anchor,
  useMobileSheet = true,
  keepMounted = false,
  children,
  ...props
}: ComboboxContentProps<ClassName>) {
  const container = usePortalContainer();
  const isMobile = useIsMobile();
  const shouldUseMobileSheet = isMobile && useMobileSheet;
  return (
    <ComboboxPrimitive.Portal
      keepMounted={keepMounted}
      container={UNSAFE_portalContainer ?? container}
    >
      <PortalThemeScope>
        {/* Phone only: a dim overlay behind the bottom sheet. */}
        {shouldUseMobileSheet && (
          <ComboboxPrimitive.Backdrop className="fixed inset-0 z-[149] bg-default/10 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0" />
        )}
        <ComboboxPrimitive.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          anchor={anchor}
          // On phones, pin the popup to the bottom edge as a full-width sheet,
          // overriding Base UI's inline anchor coordinates with `!important`
          // (including the inline `transform`, which otherwise shifts the
          // pinned sheet back to the anchor and off-screen).
          className={cn(
            "isolate z-[150]",
            shouldUseMobileSheet &&
              "fixed! inset-x-0! bottom-0! top-auto! transform-none!",
            UNSAFE_positionerClassName,
          )}
        >
          <ComboboxPrimitive.Popup
            data-slot="combobox-content"
            data-chips={Boolean(anchor)}
            className={
              shouldUseMobileSheet
                ? // Phone: plain, solid bottom-sheet drawer (no consumer glass
                  // styling — that only reads well as an anchored popover).
                  "group/combobox-content relative flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-3xl border-t-0.5 border-border bg-sheet text-default shadow-2xl duration-150 data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-open:slide-in-from-bottom-10 data-closed:slide-out-to-bottom-10"
                : // Desktop: anchored popover sized to its trigger.
                  cn(
                    "bg-sheet border border-0.5 border-border text-default data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 overflow-hidden rounded-2xl shadow-xl duration-100 data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin) data-[chips=true]:min-w-(--anchor-width)",
                    className,
                    UNSAFE_className,
                  )
            }
            {...props}
          >
            {/* Drag-handle affordance for the phone sheet. */}
            {shouldUseMobileSheet && (
              <div
                aria-hidden
                className="mx-auto mt-2.5 mb-1 h-1.5 w-10 shrink-0 rounded-full bg-surface"
              />
            )}
            <ComboboxMobileSheetContext.Provider value={shouldUseMobileSheet}>
              {children}
            </ComboboxMobileSheetContext.Provider>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </PortalThemeScope>
    </ComboboxPrimitive.Portal>
  );
}

interface ComboboxListProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.List.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ComboboxListInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: ComboboxListProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ComboboxPrimitive.List
      ref={ref}
      data-slot="combobox-list"
      className={cn(
        "flex flex-col gap-1 no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 p-1 data-empty:p-0 overflow-y-auto overscroll-contain",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ComboboxList = React.forwardRef(ComboboxListInner) as <
  ClassName extends string,
>(
  props: ComboboxListProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

export interface ComboboxItemProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Item.Props, "className" | "children">,
    Omit<ListItemContentProps, "endEnhancer"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ComboboxItemInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    startEnhancer,
    size,
    variant,
    selected,
    disabled,
    inset,
    label,
    description,
    ...props
  }: ComboboxItemProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ComboboxPrimitive.Item
      ref={ref}
      data-slot="combobox-item"
      render={
        <ListItem
          label={label}
          className={className}
          UNSAFE_className={cn(
            "aria-selected:bg-menu-item-selected",
            UNSAFE_className,
          )}
          startEnhancer={startEnhancer}
          size={size}
          variant={variant}
          selected={selected}
          disabled={disabled}
          inset={inset}
          description={description}
          endEnhancer={
            <ComboboxPrimitive.ItemIndicator>
              <CheckIcon />
            </ComboboxPrimitive.ItemIndicator>
          }
        />
      }
      disabled={disabled}
      {...props}
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ComboboxItem = React.forwardRef(ComboboxItemInner) as <
  ClassName extends string,
>(
  props: ComboboxItemProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

interface ComboboxGroupProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Group.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxGroupProps<ClassName>) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ComboboxLabelProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.GroupLabel.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxLabel<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxLabelProps<ClassName>) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn(
        "text-default header-h6 px-2 py-1",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  );
}

interface ComboboxEmptyProps<ClassName extends string = string>
  extends Omit<
      ComboboxPrimitive.Empty.Props,
      "className" | "title" | "description"
    >,
    Omit<
      EmptyProps<ClassName>,
      "className" | "UNSAFE_className" | "title" | "description"
    > {
  title?: string;
  description?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxEmpty<ClassName extends string>({
  className,
  UNSAFE_className,
  title = "No results found",
  description = "Try a different search.",
  size = "sm",
  ...props
}: ComboboxEmptyProps<ClassName>) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "text-muted hidden w-full justify-center py-6 text-center text-sm group-data-empty/combobox-content:flex",
        className,
        UNSAFE_className,
      )}
      render={<Empty size={size} title={title} description={description} />}
      {...props}
    />
  );
}

function ComboboxSeparator({
  ...props
}: Omit<ComboboxPrimitive.Separator.Props, "className">) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      render={<Separator />}
      {...props}
    />
  );
}

interface ComboboxChipsProps<ClassName extends string = string>
  extends Omit<
    React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
      ComboboxPrimitive.Chips.Props,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxChips<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxChipsProps<ClassName>) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        "border-component-border focus-within:border-ring focus-within:ring-ring/50 flex min-h-8 flex-wrap items-center gap-1 rounded-lg border bg-transparent bg-clip-padding px-2.5 py-1 text-sm transition-colors focus-within:ring-3 has-data-[slot=combobox-chip]:px-1",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ComboboxChipProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Chip.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  showRemove?: boolean;
  removeLabel?: string;
}

function ComboboxChip<ClassName extends string>({
  className,
  UNSAFE_className,
  children,
  showRemove = true,
  removeLabel = "Remove",
  ...props
}: ComboboxChipProps<ClassName>) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        "bg-surface text-default flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          aria-label={removeLabel}
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
          render={
            <Button variant="tertiary" icon size="sm">
              <XIcon className="pointer-events-none" />
            </Button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  );
}

interface ComboboxChipsInputProps<ClassName extends string = string>
  extends Omit<ComboboxPrimitive.Input.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxChipsInput<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxChipsInputProps<ClassName>) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn(
        "min-w-16 flex-1 outline-none",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

interface ComboboxHeaderProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxHeader<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxHeaderProps<ClassName>) {
  return (
    <div
      data-slot="combobox-header"
      className={cn(
        "flex flex-col gap-2 rounded-t-2xl border-border border-b-0.5 p-3 pb-4",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}
interface ComboboxTitleProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxTitle<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxTitleProps<ClassName>) {
  return (
    <div
      data-slot="combobox-title"
      className={cn("text-default header-h6", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface ComboboxFooterProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ComboboxFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ComboboxFooterProps<ClassName>) {
  return (
    <div
      data-slot="combobox-footer"
      className={cn(
        "p-3 rounded-b-2xl border-border border-t-0.5",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export type ComboboxItemWithValue = Omit<ComboboxItemProps, "value"> & {
  value: string;
};

export interface ComboboxItemGroup<
  Item extends ComboboxItemWithValue = ComboboxItemWithValue,
> {
  value: string;
  label: React.ReactNode;
  items: Readonly<Item[]>;
}

function isGroupedItems<Item extends ComboboxItemWithValue>(
  items: Readonly<Item[]> | Readonly<ComboboxItemGroup<Item>[]>,
): items is Readonly<ComboboxItemGroup<Item>[]> {
  const firstItem = items[0];
  if (!firstItem) {
    return false;
  }
  return "items" in firstItem;
}

interface ComboboxProps<
  Item extends ComboboxItemWithValue,
  ClassName extends string = string,
> extends Omit<ComboboxPrimitive.Root.Props<Item>, "children" | "items"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  UNSAFE_positionerClassName?: string;
  UNSAFE_portalContainer?: ComboboxPrimitive.Portal.Props["container"];
  title?: string;
  footer?: React.ReactNode;
  // eslint-disable-next-line replo/no-jsx-element-property-type -- cloneElement/Radix render requires ReactElement (Noah, 2026-06-26)
  trigger: React.ReactElement;
  placeholder?: string;
  empty?: React.ReactNode;
  triggerAsChild?: boolean;
  items: Readonly<Item[]> | Readonly<ComboboxItemGroup<Item>[]>;
  finalFocus?: ComboboxPrimitive.Popup.Props["finalFocus"];
}

function Combobox<
  Item extends ComboboxItemWithValue,
  ClassName extends string = string,
>({
  className,
  UNSAFE_className,
  UNSAFE_positionerClassName,
  UNSAFE_portalContainer,
  title,
  footer,
  trigger,
  items,
  placeholder,
  empty,
  triggerAsChild = false,
  finalFocus,
  ...props
}: ComboboxProps<Item, ClassName>) {
  return (
    <ComboboxRoot
      items={items}
      itemToStringValue={(item: Item) => String(item.label)}
      {...props}
    >
      {triggerAsChild ? (
        <ComboboxTrigger render={trigger} />
      ) : (
        <ComboboxTrigger>{trigger}</ComboboxTrigger>
      )}

      <ComboboxContent
        UNSAFE_className={cn(className, UNSAFE_className)}
        UNSAFE_positionerClassName={UNSAFE_positionerClassName}
        UNSAFE_portalContainer={UNSAFE_portalContainer}
        finalFocus={finalFocus}
      >
        <ComboboxHeader>
          {title && <ComboboxTitle>{title}</ComboboxTitle>}

          <ComboboxInput startEnhancer={<Search />} placeholder={placeholder} />
        </ComboboxHeader>

        <ComboboxEmpty>{empty}</ComboboxEmpty>

        <ComboboxList>
          {isGroupedItems(items)
            ? (group: ComboboxItemGroup<Item>, index: number) => (
                <React.Fragment key={group.value}>
                  {index > 0 && <ComboboxSeparator />}
                  <ComboboxGroup items={group.items}>
                    <ComboboxLabel>{group.label}</ComboboxLabel>
                    <ComboboxCollection>
                      {(item: Item) => (
                        <ComboboxItem key={item.value} {...item} value={item} />
                      )}
                    </ComboboxCollection>
                  </ComboboxGroup>
                </React.Fragment>
              )
            : (item: Item) => (
                <ComboboxItem key={item.value} {...item} value={item} />
              )}
        </ComboboxList>

        {footer && <ComboboxFooter>{footer}</ComboboxFooter>}
      </ComboboxContent>
    </ComboboxRoot>
  );
}

export {
  Combobox,
  ComboboxRoot,
  ComboboxInput,
  ComboboxContent,
  ComboboxHeader,
  ComboboxTitle,
  ComboboxFooter,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxValue,
  useComboboxAnchor,
};
